/**
 * 플레이어 아래 진단 표면의 보기 전환. 어떤 보기가 있고 무엇이 기본 보기인지 정한다.
 *
 * Owns:
 * - 보기 id 와 스위처 옵션(라벨·툴팁·비활성 사유).
 * - 사용자가 고른 보기가 없거나 고를 수 없을 때의 기본 보기.
 *
 * Does not own:
 * - 고른 보기의 수명. 이 값은 창(에피소드 탭)의 로컬 상태이고 소비처가 보관한다. 다른
 *   에피소드로 넘어가도 유지되고, URL 에는 넣지 않는다.
 * - action_source 채널 유무 판정(`@/api/diagnosticSegments`).
 */

// `timing` 은 스위처에서 숨기지만 id 는 남긴다. 창이 보관한 값이 `timing` 이어도 옵션이 없으면
// `resolveDiagnosticsView` 가 기본 보기로 바꾼다.
export type DiagnosticsViewId = "segments" | "channelBands" | "timing" | "motion" | "motionBands";

export type DiagnosticsViewOption = {
  value: DiagnosticsViewId;
  label: string;
  tooltip: string;
  disabled?: boolean;
  disabledTooltip?: string;
};

const SEGMENTS_OPTION: DiagnosticsViewOption = {
  value: "segments",
  label: "구간",
  tooltip: "action_source 가 기록한 출처(INFERENCE·INTERVENTION 등)별 구간을 봅니다.",
};

const FIXED_OPTIONS: readonly DiagnosticsViewOption[] = [
  {
    value: "channelBands",
    label: "채널 밴드",
    tooltip:
      "채널마다 이 에피소드의 trajectory 를 전체 에피소드의 기준 통로(지점별 p10–p90)와 겹쳐 봅니다.",
  },
  // 타이밍 보기는 용도가 아직 정해지지 않았다. 그래서 **화면에서만 뺀다**(2026-09-12).
  // 모델·보기(`TimingView`·`timingRows`)와 id 는 그대로 둔다. 그래서 이 항목만 다시 추가하면
  // 보기가 돌아온다. prod 배포 뒤에 개발 쪽에서 용도를 다시 정하기로 했다. 그때까지 지우지 않는다.
  // {
  //   value: "timing",
  //   label: "타이밍",
  //   tooltip: "채널마다 기록 간격과 빈틈을 봅니다.",
  // },
  // 운동은 두 보기다. 시간별 곡선과 저장된 요약 통계다. 같은 데이터를 두 방식으로 보여 주므로
  // 두 보기는 항상 이웃해야 한다. 주석 처리한 `타이밍` 을 다시 추가해도 그 사이에 넣지 않는다.
  {
    value: "motion",
    label: "운동 지표",
    tooltip: "채널마다 시간별 속력·가속도 크기를 Observation·Action 을 겹쳐 확대 창에서 봅니다.",
  },
  {
    value: "motionBands",
    label: "운동 밴드",
    tooltip: "채널마다 에피소드 전체와 구간의 저장된 요약 통계를 지표별 공유 축 위 막대로 봅니다.",
  },
];

/**
 * 스위처 옵션. 구간 보기는 데이터셋에 action_source 채널이 없을 때만 비활성으로 둔다. 로딩 중이거나
 * 아직 산출 전이면 보기 안에서 알리고, 옵션 모양은 바꾸지 않는다. 그래서 에피소드를 바꿀 때
 * 스위처 폭이 바뀌지 않는다.
 */
export function diagnosticsViewOptions(
  hasActionSource: boolean | null,
): readonly DiagnosticsViewOption[] {
  const segments =
    hasActionSource === false
      ? {
          ...SEGMENTS_OPTION,
          disabled: true,
          disabledTooltip: "이 Dataset에는 action_source 채널이 없어 구간을 나눌 수 없습니다.",
        }
      : SEGMENTS_OPTION;
  return [segments, ...FIXED_OPTIONS];
}

/**
 * 지금 그릴 보기를 정한다:
 * - 고른 보기가 있고 고를 수 있으면 그 보기다.
 * - 아니면, action_source 채널이 있는 데이터셋은 구간 보기다.
 * - 나머지는 채널 밴드 보기다.
 *
 * 폴백은 고른 값을 덮어쓰지 않는다. 채널 없는 데이터셋에서 잠시 채널 밴드 보기가 되어도, 채널이
 * 있는 데이터셋으로 돌아오면 고른 구간 보기가 다시 표시된다.
 */
export function resolveDiagnosticsView(
  choice: DiagnosticsViewId | null,
  hasActionSource: boolean | null,
): DiagnosticsViewId {
  const options = diagnosticsViewOptions(hasActionSource);
  if (choice !== null && options.some((option) => option.value === choice && !option.disabled)) {
    return choice;
  }
  return hasActionSource === true ? "segments" : "channelBands";
}
